import { z } from 'zod';

import { AI_PROVIDER_IDS } from './ai-catalog';
import { PLATFORM_IDS } from './platform-catalog';

/**
 * The single source of truth for every status vocabulary in the system.
 *
 * These names are duplicated as Prisma enums in `packages/database/prisma/schema.prisma`
 * and MUST stay character-for-character identical: the API casts straight between
 * the two, so a mismatch is a runtime error the type system cannot catch.
 *
 * Each vocabulary is declared once as a `const` array, then derived into a
 * TypeScript union and a Zod enum, so adding a member is a one-line change that
 * propagates everywhere.
 */

// ---------------------------------------------------------------------------
// Platform
// ---------------------------------------------------------------------------

/** Re-export of the catalog ids — `platform-catalog.ts` is where a platform is
 *  described; this file only lends it the enum treatment every vocabulary gets. */
export const PLATFORMS = PLATFORM_IDS;
export const platformSchema = z.enum(PLATFORMS);
export type Platform = (typeof PLATFORMS)[number];

/** Where a post was found. Drives both scoring and dashboard filters. */
export const POST_SOURCE_TYPES = [
  'PROFILE',
  'PAGE',
  'GROUP',
  'MARKETPLACE',
  'EVENT',
  'UNKNOWN',
] as const;
export const postSourceTypeSchema = z.enum(POST_SOURCE_TYPES);
export type PostSourceType = (typeof POST_SOURCE_TYPES)[number];

// ---------------------------------------------------------------------------
// Accounts & billing
// ---------------------------------------------------------------------------

/**
 * Two roles, deliberately. OWNER existed alongside ADMIN from the first schema
 * and the two were checked together in every single gate — `@Roles('OWNER',
 * 'ADMIN')` with no route, view or rule that told them apart. A distinction the
 * product never uses is a distinction someone will eventually get wrong, so
 * OWNER was merged into ADMIN (28/08/2026). ADMIN is full control; MEMBER is
 * the day-to-day operator: pair an agent, log platform accounts in, work the
 * comment queue — never calibration tooling or workspace-wide settings.
 */
export const USER_ROLES = ['ADMIN', 'MEMBER'] as const;
export const userRoleSchema = z.enum(USER_ROLES);
export type UserRole = (typeof USER_ROLES)[number];

export const USER_STATUSES = ['PENDING_VERIFICATION', 'ACTIVE', 'SUSPENDED', 'DELETED'] as const;
export const userStatusSchema = z.enum(USER_STATUSES);
export type UserStatus = (typeof USER_STATUSES)[number];

export const SUBSCRIPTION_PLANS = ['TRIAL', 'STARTER', 'PRO', 'ENTERPRISE'] as const;
export const subscriptionPlanSchema = z.enum(SUBSCRIPTION_PLANS);
export type SubscriptionPlan = (typeof SUBSCRIPTION_PLANS)[number];

export const SUBSCRIPTION_STATUSES = [
  'TRIALING',
  'ACTIVE',
  'PAST_DUE',
  'CANCELLED',
  'EXPIRED',
] as const;
export const subscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUSES);
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

// ---------------------------------------------------------------------------
// Agent · Browser · Project · Search — the four state machines
// ---------------------------------------------------------------------------

/** Lifecycle of a paired desktop agent. */
export const AGENT_STATUSES = ['UNPAIRED', 'OFFLINE', 'ONLINE', 'UPDATING', 'ERROR'] as const;
export const agentStatusSchema = z.enum(AGENT_STATUSES);
export type AgentStatus = (typeof AGENT_STATUSES)[number];

/**
 * Lifecycle of one Chromium browser profile.
 *
 * `USER_ACTION_REQUIRED` is the terminal state for every anti-automation signal
 * we encounter — checkpoint, 2FA, CAPTCHA, account restriction. The system stops
 * and asks the human. It never attempts to solve or bypass any of them.
 */
export const BROWSER_STATUSES = [
  'STOPPED',
  'STARTING',
  'LOGIN_REQUIRED',
  'READY',
  'RUNNING',
  'PAUSED',
  'USER_ACTION_REQUIRED',
  'CRASHED',
] as const;
export const browserStatusSchema = z.enum(BROWSER_STATUSES);
export type BrowserStatus = (typeof BROWSER_STATUSES)[number];

/**
 * What the agent observed about a platform session. Never carries credentials.
 *
 * One vocabulary for every platform on purpose: the states are properties of a
 * *login session under automation pressure* (logged out, checkpoint, 2FA,
 * restricted), not of any one site — Threads (via Instagram) walks users through
 * the same gauntlet. What differs per platform is how each state is *detected*,
 * and that lives in the SelectorPack detectors, which are per-platform data.
 */
export const PLATFORM_SESSION_STATUSES = [
  'UNKNOWN',
  'LOGGED_OUT',
  'LOGGED_IN',
  'CHECKPOINT',
  'TWO_FACTOR_REQUIRED',
  'RESTRICTED',
] as const;
export const platformSessionStatusSchema = z.enum(PLATFORM_SESSION_STATUSES);
export type PlatformSessionStatus = (typeof PLATFORM_SESSION_STATUSES)[number];

/** @deprecated Facebook-era name — use `PLATFORM_SESSION_STATUSES`. */
export const FACEBOOK_SESSION_STATUSES = PLATFORM_SESSION_STATUSES;
/** @deprecated Facebook-era name — use `platformSessionStatusSchema`. */
export const facebookSessionStatusSchema = platformSessionStatusSchema;
/** @deprecated Facebook-era name — use `PlatformSessionStatus`. */
export type FacebookSessionStatus = PlatformSessionStatus;

export const PROJECT_STATUSES = [
  'DRAFT',
  'READY',
  'STARTING',
  'RUNNING',
  'PAUSED',
  'STOPPING',
  'STOPPED',
  'ERROR',
] as const;
export const projectStatusSchema = z.enum(PROJECT_STATUSES);
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const SEARCH_CYCLE_STATUSES = [
  'RUNNING',
  'WAITING_NETWORK',
  'SYNCING',
  'COMPLETED',
  'FAILED',
] as const;
export const searchCycleStatusSchema = z.enum(SEARCH_CYCLE_STATUSES);
export type SearchCycleStatus = (typeof SEARCH_CYCLE_STATUSES)[number];

export const SEARCH_QUERY_STATUSES = [
  'PENDING',
  'RUNNING',
  'COMPLETED',
  'FAILED',
  'SKIPPED',
] as const;
export const searchQueryStatusSchema = z.enum(SEARCH_QUERY_STATUSES);
export type SearchQueryStatus = (typeof SEARCH_QUERY_STATUSES)[number];

export const POST_BATCH_STATUSES = ['RECEIVED', 'PROCESSED', 'FAILED'] as const;
export const postBatchStatusSchema = z.enum(POST_BATCH_STATUSES);
export type PostBatchStatus = (typeof POST_BATCH_STATUSES)[number];

// ---------------------------------------------------------------------------
// Keywords & posts
// ---------------------------------------------------------------------------

export const KEYWORD_MATCH_TYPES = ['EXACT', 'PHRASE', 'BROAD'] as const;
export const keywordMatchTypeSchema = z.enum(KEYWORD_MATCH_TYPES);
export type KeywordMatchType = (typeof KEYWORD_MATCH_TYPES)[number];

/** Human review state of a post *within one project*. Lives on `post_matches`. */
export const POST_REVIEW_STATUSES = [
  'NEW',
  'VIEWED',
  'RELEVANT',
  'IRRELEVANT',
  'BLOCKED',
  'ARCHIVED',
] as const;
export const postReviewStatusSchema = z.enum(POST_REVIEW_STATUSES);
export type PostReviewStatus = (typeof POST_REVIEW_STATUSES)[number];

/** Which dedup rule matched. Recorded so ingestion decisions stay explainable. */
export const DEDUP_MATCH_REASONS = [
  'NEW',
  'CANONICAL_URL',
  'EXTERNAL_POST_ID',
  'AUTHOR_TIME_CONTENT',
  'NORMALIZED_CONTENT',
  'SIMILARITY_HASH',
] as const;
export const dedupMatchReasonSchema = z.enum(DEDUP_MATCH_REASONS);
export type DedupMatchReason = (typeof DEDUP_MATCH_REASONS)[number];

// ---------------------------------------------------------------------------
// AI outputs
// ---------------------------------------------------------------------------

export const POST_INTENTS = [
  'SEEKING_SERVICE',
  'SEEKING_PRODUCT',
  'PRICE_INQUIRY',
  'RECOMMENDATION_REQUEST',
  'COMPARISON',
  'COMPLAINT',
  'SELLING',
  'GENERAL_DISCUSSION',
  'UNRELATED',
] as const;
export const postIntentSchema = z.enum(POST_INTENTS);
export type PostIntent = (typeof POST_INTENTS)[number];

export const SENTIMENTS = ['POSITIVE', 'NEUTRAL', 'NEGATIVE', 'MIXED'] as const;
export const sentimentSchema = z.enum(SENTIMENTS);
export type Sentiment = (typeof SENTIMENTS)[number];

export const URGENCY_LEVELS = ['NONE', 'LOW', 'MEDIUM', 'HIGH'] as const;
export const urgencySchema = z.enum(URGENCY_LEVELS);
export type Urgency = (typeof URGENCY_LEVELS)[number];

export const RECOMMENDED_ACTIONS = [
  'IGNORE',
  'MONITOR',
  'REVIEW_MANUALLY',
  'COMMENT',
  'PRIORITY_CONTACT',
] as const;
export const recommendedActionSchema = z.enum(RECOMMENDED_ACTIONS);
export type RecommendedAction = (typeof RECOMMENDED_ACTIONS)[number];

/**
 * Re-exported from the catalog rather than re-declared.
 *
 * There is exactly one list of provider ids in the system and it is
 * `AI_PROVIDER_IDS` in `ai-catalog.ts`, next to the metadata that describes each
 * one. Declaring the ids here as well would let a provider exist as an enum
 * member with no display name, no model list and no key check — which is how a
 * settings screen ends up offering a provider that cannot actually be configured.
 */
export const AI_PROVIDERS = AI_PROVIDER_IDS;
export const aiProviderSchema = z.enum(AI_PROVIDERS);
export type AiProvider = (typeof AI_PROVIDERS)[number];

/**
 * The outcome of asking a provider whether a key works.
 *
 * These are kept apart because each one needs a *different action* from the user,
 * and collapsing them is the specific failure this vocabulary exists to prevent:
 * reporting a provider outage as "khoá sai" sends someone hunting for a problem
 * with a key that is perfectly fine, and reporting a wrong key as a transient
 * error gets it saved and left to fail silently for hours.
 *
 *  - `VALID`                — key authenticated and the chosen model is reachable.
 *  - `KEY_REJECTED`         — 401/403. The key is wrong, revoked, or lacks scope.
 *  - `MODEL_NOT_AVAILABLE`  — key is fine, this account cannot use that model.
 *  - `RATE_LIMITED`         — 429. Nothing was proved either way.
 *  - `UNREACHABLE`          — network, timeout or 5xx. Nothing was proved either way.
 *  - `NOT_CHECKED`          — the key was stored without a check, on request.
 */
export const AI_KEY_VALIDATION_STATUSES = [
  'VALID',
  'KEY_REJECTED',
  'MODEL_NOT_AVAILABLE',
  'RATE_LIMITED',
  'UNREACHABLE',
  'NOT_CHECKED',
] as const;
export const aiKeyValidationStatusSchema = z.enum(AI_KEY_VALIDATION_STATUSES);
export type AiKeyValidationStatus = (typeof AI_KEY_VALIDATION_STATUSES)[number];

export const AI_REQUEST_KINDS = [
  'POST_SCORING',
  'COMMENT_GENERATION',
  'SELECTOR_SUGGESTION',
  /**
   * Reading a post's publication time off a screenshot of the timestamp label.
   *
   * Its own kind rather than a flavour of `POST_SCORING`, because the two answer
   * different questions about cost: a scoring call is one per post above the
   * tier-1 threshold and is the product working as designed, while a time call
   * only ever happens because the parser failed. Counting them together would
   * hide exactly the number an operator needs to see rising — that is the number
   * that says the SelectorPack needs recalibrating.
   */
  'POST_TIME_EXTRACTION',
] as const;
export const aiRequestKindSchema = z.enum(AI_REQUEST_KINDS);
export type AiRequestKind = (typeof AI_REQUEST_KINDS)[number];

export const AI_REQUEST_STATUSES = [
  'PENDING',
  'SUCCESS',
  'FAILED',
  'SKIPPED',
  'RATE_LIMITED',
] as const;
export const aiRequestStatusSchema = z.enum(AI_REQUEST_STATUSES);
export type AiRequestStatus = (typeof AI_REQUEST_STATUSES)[number];

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

/**
 * How much autonomy the operator has granted for a project.
 *
 * Defaults are `MANUAL` / `AI_DRAFT`. The two automatic modes must be switched on
 * deliberately by the user and remain subject to the policy engine and both
 * rate limiters.
 */
export const COMMENT_MODES = ['OFF', 'MANUAL', 'AI_DRAFT', 'TEMPLATE_AUTO', 'AI_AUTO'] as const;
export const commentModeSchema = z.enum(COMMENT_MODES);
export type CommentMode = (typeof COMMENT_MODES)[number];

export const COMMENT_JOB_STATUSES = [
  'PENDING',
  'WAITING_APPROVAL',
  'APPROVED',
  'QUEUED',
  'SENT_TO_AGENT',
  'EXECUTING',
  'SUCCESS',
  'FAILED',
  'SKIPPED',
  'CANCELLED',
  'USER_ACTION_REQUIRED',
] as const;
export const commentJobStatusSchema = z.enum(COMMENT_JOB_STATUSES);
export type CommentJobStatus = (typeof COMMENT_JOB_STATUSES)[number];

export const COMMENT_SOURCES = ['MANUAL', 'TEMPLATE', 'AI'] as const;
export const commentSourceSchema = z.enum(COMMENT_SOURCES);
export type CommentSource = (typeof COMMENT_SOURCES)[number];

/**
 * Why the policy engine, a validator or the agent refused to send a comment.
 *
 * `COMMAND_REVOKED` is the newest member and it exists because it was missing.
 * When an operator cancels a job, `CommentsService.cancelJob` revokes the
 * `comment.execute` command, and the agent — on receiving a withdrawn frame, or
 * on seeing `local.revoked` in its own guard — has to name the refusal with a
 * member of this closed list. There was none that meant "a control stopped this,
 * nothing was typed", so the agent borrowed `EMERGENCY_STOP`. That code travels
 * on `comment.result`, is stored, and the dashboard renders it as a red chip
 * reading "Đang bật dừng khẩn cấp" — asserting that the workspace kill switch is
 * on when it is not, next to the only button that would turn it off. A borrowed
 * enum member is a lie with a schema.
 */
export const COMMENT_REJECTION_REASONS = [
  'CONTENT_TOO_LONG',
  'CONTENT_TOO_SHORT',
  'PROHIBITED_LINK',
  'PHONE_NUMBER',
  'SENSITIVE_WORD',
  'DUPLICATE_CONTENT',
  'OUT_OF_SCOPE',
  'LOW_CONFIDENCE',
  'POST_NO_LONGER_ELIGIBLE',
  'RATE_LIMIT_10M',
  'RATE_LIMIT_1H',
  'RATE_LIMIT_1D',
  'RATE_LIMIT_PROJECT_1D',
  'MIN_INTERVAL',
  'AGENT_LOCAL_GUARD',
  'EMERGENCY_STOP',
  'SUBSCRIPTION_LIMIT',
  'PROFILE_MISMATCH',
  'BROWSER_NOT_READY',
  'COMMAND_REVOKED',
] as const;
export const commentRejectionReasonSchema = z.enum(COMMENT_REJECTION_REASONS);
export type CommentRejectionReason = (typeof COMMENT_REJECTION_REASONS)[number];

/**
 * Members this vocabulary has that `enum CommentRejectionReason` in
 * `schema.prisma` does not. Empty, and kept as a named type rather than deleted.
 *
 * It was `'COMMAND_REVOKED'` for exactly as long as that member existed here
 * without existing in Postgres. The rule at the top of this file — contract enum
 * and Prisma enum stay character-for-character identical — could not be met in
 * one step, because adding the member to the native type needs `ALTER TYPE`, and
 * shipping it without one would mean the first revoked comment writes a value
 * Postgres has never heard of: the write throws, and a job somebody explicitly
 * cancelled sits in SENT_TO_AGENT for ever. That is worse than the wrong chip
 * the member was added to fix, so the gap was declared instead of assumed.
 *
 * `20260812004605_comment_rejection_command_revoked` closed it. The union is now
 * `never`, which makes {@link toPersistedRejectionReason} the identity — and the
 * scaffolding stays because the next member added ahead of its migration needs
 * somewhere to be declared, and a name that already means "not storable yet"
 * beats inventing one under time pressure.
 */
export type UnpersistedCommentRejectionReason = never;

/** The subset a native enum column can store. Currently all of them. */
export type PersistedCommentRejectionReason = Exclude<
  CommentRejectionReason,
  UnpersistedCommentRejectionReason
>;

/**
 * Narrow a reason to what a native enum column will accept, or `null`.
 *
 * The identity today. Every caller writing to `comment_jobs.rejection_reason` or
 * `comment_candidates.rejection_reason` still goes through it, deliberately: the
 * funnel is what makes the next unpersistable member a one-line change here
 * instead of a hunt for every write site.
 */
export function toPersistedRejectionReason(
  reason: CommentRejectionReason | null | undefined,
): PersistedCommentRejectionReason | null {
  return reason ?? null;
}

/**
 * WHY a draft is waiting for a person — as separate facts, never as one boolean.
 *
 * `comment_candidates.requires_manual_review` used to be the only answer, and it
 * is the OR of three unrelated things. Two of them describe THIS draft; the
 * third describes the project's configuration and would be true of a perfect
 * draft. Since `require_approval` defaults to true, that boolean is set on
 * essentially every candidate the product produces by default, so the dashboard
 * put a red warning block on 100% of drafts and operators learned to click past
 * it — including on the one draft in a hundred where the model had invented a
 * price.
 *
 * Separated, each flag leads somewhere different:
 *
 *  - `PROJECT_REQUIRES_APPROVAL` — the project is in AI_DRAFT, or its
 *    `require_approval` switch is on. Says nothing at all about the text. The
 *    remedy is a settings change, not an edit.
 *  - `LOW_CONFIDENCE` — the model's own self-assessment is under
 *    `min_ai_confidence`. About this draft.
 *  - `UNSUPPORTED_CLAIM` — the draft states a price, a discount, a delivery
 *    time, a guarantee or a superlative that appears nowhere in the material the
 *    operator supplied. About this draft, and the only one of the three that
 *    describes a sentence which must not be published under a real business's
 *    name unread. `comment_candidates.unsupported_claims` carries the quotes.
 *
 * The distinction has teeth beyond presentation: `CommentsService` refuses to
 * treat a draft carrying either of the two draft-level flags as automatic, while
 * `PROJECT_REQUIRES_APPROVAL` is already handled by the policy engine's
 * `requiresApproval`. Collapsed into one boolean that separation was not
 * expressible, and an AI_AUTO project published invented prices unread.
 */
export const COMMENT_REVIEW_FLAGS = [
  'PROJECT_REQUIRES_APPROVAL',
  'LOW_CONFIDENCE',
  'UNSUPPORTED_CLAIM',
] as const;
export const commentReviewFlagSchema = z.enum(COMMENT_REVIEW_FLAGS);
export type CommentReviewFlag = (typeof COMMENT_REVIEW_FLAGS)[number];

/**
 * Shapes of claim a reviewer is asked to check, mirrored from `CLAIM_PATTERNS`
 * in `comment-validation.ts`.
 *
 * Declared here because this file is where a vocabulary lives once, and because
 * the kinds now travel on the wire and are stored: `UnsupportedClaim['kind']`
 * was a bare literal union inside the validator, which is fine for a pure
 * function and not fine for a column. `unsupportedClaimSchema` in
 * `rest/comments.ts` proves the two still agree at compile time.
 */
export const UNSUPPORTED_CLAIM_KINDS = [
  'PRICE',
  'DISCOUNT',
  'DELIVERY_TIME',
  'WARRANTY',
  'SUPERLATIVE',
] as const;
export const unsupportedClaimKindSchema = z.enum(UNSUPPORTED_CLAIM_KINDS);
export type UnsupportedClaimKind = (typeof UNSUPPORTED_CLAIM_KINDS)[number];

/**
 * How far a "Huỷ job" actually reaches.
 *
 * Cancelling used to write CANCELLED to the row and answer with a green toast,
 * for every state the transition table allows — including SENT_TO_AGENT, where
 * the `comment.execute` frame is already on the operator's own machine and the
 * agent may be typing into Facebook at that moment. The operator read "Đã huỷ
 * job" and walked away believing they had stopped a comment that then appeared
 * on a stranger's post under their real name.
 *
 * The server cannot make one promise cover all four situations, so it stops
 * making one:
 *
 *  - `NOTHING_TO_RECALL` — no command was ever issued for this job. Nothing can
 *    execute. The cancellation is complete.
 *  - `COMMAND_REVOKED_BEFORE_SEND` — a command row exists but the frame was
 *    never written to a socket, and the server never redelivers a command. It is
 *    marked revoked and can no longer be executed. The cancellation is complete.
 *  - `RECALL_SENT` — the frame had already reached the agent. A `command.revoked`
 *    notice was written to the same socket, which stops the agent only if it has
 *    not started typing yet. NOT a guarantee.
 *  - `RECALL_UNDELIVERABLE` — the frame had already reached the agent and the
 *    agent is not reachable now, so not even the notice went out. Nothing was
 *    recalled. NOT a guarantee.
 *
 * Only the first two mean the comment provably will not be posted; that is what
 * `guaranteedNotPosted` carries, and it is the field a client must render on.
 */
export const COMMENT_CANCEL_RECALL_OUTCOMES = [
  'NOTHING_TO_RECALL',
  'COMMAND_REVOKED_BEFORE_SEND',
  'RECALL_SENT',
  'RECALL_UNDELIVERABLE',
] as const;
export const commentCancelRecallOutcomeSchema = z.enum(COMMENT_CANCEL_RECALL_OUTCOMES);
export type CommentCancelRecallOutcome = (typeof COMMENT_CANCEL_RECALL_OUTCOMES)[number];

/** The two outcomes in which the server can promise nothing will be posted. */
export const GUARANTEED_COMMENT_CANCEL_OUTCOMES: readonly CommentCancelRecallOutcome[] = [
  'NOTHING_TO_RECALL',
  'COMMAND_REVOKED_BEFORE_SEND',
] as const;

// ---------------------------------------------------------------------------
// Commands, media, selector packs, audit
// ---------------------------------------------------------------------------

export const AGENT_COMMAND_STATUSES = [
  'PENDING',
  'SENT',
  'ACKNOWLEDGED',
  'COMPLETED',
  'FAILED',
  'REJECTED',
  'EXPIRED',
] as const;
export const agentCommandStatusSchema = z.enum(AGENT_COMMAND_STATUSES);
export type AgentCommandStatus = (typeof AGENT_COMMAND_STATUSES)[number];

/**
 * What a stored object is — this drives its retention policy.
 *
 * `COMMENT_TEMPLATE_IMAGE` is kept for as long as the template exists.
 * `DIAGNOSTIC_SCREENSHOT` and `DOM_SNAPSHOT` self-expire (7-30 days) because they
 * come off a user's machine and have no long-term value.
 */
export const MEDIA_OBJECT_KINDS = [
  'COMMENT_TEMPLATE_IMAGE',
  'DIAGNOSTIC_SCREENSHOT',
  'DOM_SNAPSHOT',
  'POST_MEDIA_REFERENCE',
] as const;
export const mediaObjectKindSchema = z.enum(MEDIA_OBJECT_KINDS);
export type MediaObjectKind = (typeof MEDIA_OBJECT_KINDS)[number];

export const MEDIA_OBJECT_STATUSES = ['PENDING_UPLOAD', 'READY', 'EXPIRED', 'DELETED'] as const;
export const mediaObjectStatusSchema = z.enum(MEDIA_OBJECT_STATUSES);
export type MediaObjectStatus = (typeof MEDIA_OBJECT_STATUSES)[number];

export const SELECTOR_PACK_VERSION_STATUSES = [
  'DRAFT',
  'PUBLISHED',
  'ROLLED_BACK',
  'ARCHIVED',
] as const;
export const selectorPackVersionStatusSchema = z.enum(SELECTOR_PACK_VERSION_STATUSES);
export type SelectorPackVersionStatus = (typeof SELECTOR_PACK_VERSION_STATUSES)[number];

/** Outcome of resolving one selector rule — the raw signal behind selector health. */
export const SELECTOR_HEALTH_OUTCOMES = ['SUCCESS', 'FALLBACK_USED', 'FAILED'] as const;
export const selectorHealthOutcomeSchema = z.enum(SELECTOR_HEALTH_OUTCOMES);
export type SelectorHealthOutcome = (typeof SELECTOR_HEALTH_OUTCOMES)[number];

export const AGENT_EVENT_LEVELS = ['DEBUG', 'INFO', 'WARN', 'ERROR'] as const;
export const agentEventLevelSchema = z.enum(AGENT_EVENT_LEVELS);
export type AgentEventLevel = (typeof AGENT_EVENT_LEVELS)[number];

export const AUDIT_ACTOR_TYPES = ['USER', 'AGENT', 'SYSTEM'] as const;
export const auditActorTypeSchema = z.enum(AUDIT_ACTOR_TYPES);
export type AuditActorType = (typeof AUDIT_ACTOR_TYPES)[number];

export const USAGE_METRICS = [
  'POSTS_INGESTED',
  'AI_SCORING_REQUESTS',
  'AI_COMMENT_REQUESTS',
  'COMMENTS_SENT',
  'SEARCH_CYCLES',
  'STORAGE_BYTES',
] as const;
export const usageMetricSchema = z.enum(USAGE_METRICS);
export type UsageMetric = (typeof USAGE_METRICS)[number];

// ---------------------------------------------------------------------------
// Transition tables — the state machines, expressed as data
// ---------------------------------------------------------------------------

/**
 * Legal project transitions. Both the API and the dashboard consult this, so an
 * illegal transition is rejected before it becomes a command to an agent.
 */
export const PROJECT_TRANSITIONS: Readonly<Record<ProjectStatus, readonly ProjectStatus[]>> = {
  DRAFT: ['READY', 'DRAFT'],
  READY: ['STARTING', 'DRAFT', 'STOPPED'],
  STARTING: ['RUNNING', 'ERROR', 'STOPPED'],
  RUNNING: ['PAUSED', 'STOPPING', 'ERROR'],
  PAUSED: ['RUNNING', 'STOPPING', 'ERROR', 'STOPPED'],
  STOPPING: ['STOPPED', 'ERROR'],
  STOPPED: ['STARTING', 'READY', 'DRAFT'],
  ERROR: ['READY', 'STOPPED', 'DRAFT'],
} as const;

export function canTransitionProject(from: ProjectStatus, to: ProjectStatus): boolean {
  return PROJECT_TRANSITIONS[from].includes(to);
}

/**
 * Statuses in which a project's browser-profile assignment is EXCLUSIVE: a
 * cycle is running, winding up or winding down on that Chromium profile, so no
 * other project may claim it.
 *
 * Outside these statuses the assignment is only a memory — kept so the project
 * can restart on the same profile without re-picking — and any other project may
 * take the profile over simply by starting with it. This is what makes
 * "Stop project cũ → Start project mới với cùng profile" a two-click flow
 * instead of a trip through the API to release the assignment first.
 *
 * Both the API (start / assignment endpoints) and the dashboard (profile picker)
 * consult this list, so what the picker offers is exactly what the server accepts.
 */
export const PROFILE_HOLDING_PROJECT_STATUSES = [
  'STARTING',
  'RUNNING',
  'PAUSED',
  'STOPPING',
] as const satisfies readonly ProjectStatus[];

/** True while `status` gives the project exclusive claim on its browser profile. */
export function projectHoldsProfile(status: ProjectStatus): boolean {
  return (PROFILE_HOLDING_PROJECT_STATUSES as readonly ProjectStatus[]).includes(status);
}

export const COMMENT_JOB_TRANSITIONS: Readonly<
  Record<CommentJobStatus, readonly CommentJobStatus[]>
> = {
  PENDING: ['WAITING_APPROVAL', 'APPROVED', 'SKIPPED', 'CANCELLED', 'FAILED'],
  WAITING_APPROVAL: ['APPROVED', 'CANCELLED', 'SKIPPED'],
  APPROVED: ['QUEUED', 'CANCELLED', 'SKIPPED'],
  QUEUED: ['SENT_TO_AGENT', 'CANCELLED', 'FAILED'],
  /**
   * SUCCESS is reachable from here, not only from EXECUTING.
   *
   * The obvious path is SENT_TO_AGENT → EXECUTING → SUCCESS, and modelling only
   * that assumed `comment.started` always arrives before `comment.result`. It
   * does not have to: the frame can be lost, or the API can restart between the
   * two. When that happened the success was clamped down to FAILED — and FAILED
   * is re-queueable, so a comment that HAD been posted invited an operator to
   * post it a second time on a real person's post.
   *
   * A missing progress report is not evidence that the work failed. The agent is
   * the only witness to what reached Facebook, and when it says the comment went
   * out, that is the fact this row must record.
   */
  SENT_TO_AGENT: [
    'EXECUTING',
    'SUCCESS',
    'FAILED',
    'CANCELLED',
    'USER_ACTION_REQUIRED',
    'SKIPPED',
  ],
  EXECUTING: ['SUCCESS', 'FAILED', 'USER_ACTION_REQUIRED'],
  USER_ACTION_REQUIRED: ['QUEUED', 'CANCELLED', 'SKIPPED'],
  SUCCESS: [],
  FAILED: ['QUEUED', 'CANCELLED'],
  SKIPPED: [],
  CANCELLED: [],
} as const;

export function canTransitionCommentJob(
  from: CommentJobStatus,
  to: CommentJobStatus,
): boolean {
  return COMMENT_JOB_TRANSITIONS[from].includes(to);
}

/** Browser states in which no automation may run. */
export const BROWSER_BLOCKED_STATUSES: readonly BrowserStatus[] = [
  'STOPPED',
  'STARTING',
  'LOGIN_REQUIRED',
  'USER_ACTION_REQUIRED',
  'CRASHED',
] as const;

/** Session states that require a human and must pause the project. */
export const SESSION_STATUSES_REQUIRING_USER: readonly PlatformSessionStatus[] = [
  'LOGGED_OUT',
  'CHECKPOINT',
  'TWO_FACTOR_REQUIRED',
  'RESTRICTED',
] as const;
